/**
 * Isolates each bulk schedule title and status row so modal state management
 * stays separate from schedule display, validation, and accessibility details.
 */
import React from 'react';
import { DateTime } from 'luxon';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import { useTranslation } from 'src/arb-wrapper';
import { ScheduleInputGroup } from 'src/components/assets/ScheduleInputGroup';
import { formatUtcDateTime } from 'src/utils/formatDateTime';
import { formatLiveStatus } from 'src/utils/formatLiveStatus';
import type {
  BulkScheduleGroup,
  Schedule,
} from 'src/utils/groupAssetsForBulkSchedule';

interface BulkScheduleGroupRowProps {
  readonly group: BulkScheduleGroup;
  readonly isExpanded: boolean;
  readonly schedule: Schedule;
  readonly onChange: (field: keyof Schedule, value: string) => void;
  readonly onReset: () => void;
  readonly onToggle: () => void;
}
export type ValidationErrorKey =
  | 'asset_schedule_bulk_error_missing_timestamp'
  | 'asset_schedule_bulk_error_end_after_start'
  | undefined;
export function getInitialSchedule (group: BulkScheduleGroup): Schedule {
  return group.currentSchedule && group.currentSchedule !== 'mixed'
    ? { ...group.currentSchedule }
    : { startTime: '', endTime: '' };
}
function areInstantsEqual (current: string, initial: string): boolean {
  if (current === '' || initial === '') return current === initial;
  return DateTime.fromISO(current).toMillis() ===
    DateTime.fromISO(initial).toMillis();
}
export function isScheduleDirty (
  group: BulkScheduleGroup,
  schedule: Schedule,
): boolean {
  const initial = getInitialSchedule(group);
  return !areInstantsEqual(schedule.startTime, initial.startTime) ||
    !areInstantsEqual(schedule.endTime, initial.endTime);
}
export function getScheduleValidationError (
  schedule: Schedule,
  isDirty: boolean,
): ValidationErrorKey {
  if (!isDirty) return undefined;
  if (!schedule.startTime || !schedule.endTime) {
    return 'asset_schedule_bulk_error_missing_timestamp';
  }
  const startMillis = DateTime.fromISO(schedule.startTime).toMillis();
  const endMillis = DateTime.fromISO(schedule.endTime).toMillis();
  return endMillis <= startMillis
    ? 'asset_schedule_bulk_error_end_after_start'
    : undefined;
}
export const BulkScheduleGroupRow: React.FC<BulkScheduleGroupRowProps> = ({
  group,
  isExpanded,
  schedule,
  onChange,
  onReset,
  onToggle,
}): React.ReactElement => {
  const { t } = useTranslation();
  const isDirty = isScheduleDirty(group, schedule);
  const errorKey = getScheduleValidationError(schedule, isDirty);
  const isMixed = !isDirty && group.currentSchedule === 'mixed';
  const contentId = `bulk-schedule-group-content-${group.groupId}`;
  const errorId = `bulk-schedule-group-error-${group.groupId}`;
  const toggleId = `bulk-schedule-group-toggle-${group.groupId}`;
  const liveStatus = group.liveStatus === 'UNKNOWN'
    ? t('asset_schedule_bulk_unknown_live_status')
    : formatLiveStatus(group.liveStatus);
  const fallback = t('asset_schedule_bulk_event_time_unavailable');
  const toggleLabelKey = isExpanded
    ? 'asset_schedule_bulk_collapse_group'
    : 'asset_schedule_bulk_expand_group';
  let scheduleSummary = fallback;
  if (isMixed) {
    scheduleSummary = t('asset_schedule_bulk_mixed_values');
  } else if (schedule.startTime || schedule.endTime) {
    scheduleSummary = t('asset_schedule_bulk_time_range', {
      end: formatUtcDateTime(schedule.endTime, fallback),
      start: formatUtcDateTime(schedule.startTime, fallback),
    });
  }

  return (
    <Box
      aria-describedby={errorKey ? errorId : undefined}
      aria-labelledby={toggleId}
      data-testid={`bulk-schedule-group-${group.groupId}`}
      role="group"
      spacingInset="300"
      type="outline"
      width="100%"
    >
      <Column spacing="200">
        <Row alignmentHorizontal="justify" alignmentVertical="center" wrap="down">
          <Column spacing="100">
            <Row alignmentVertical="center" spacing="200" wrap="down">
              <Tag type="neutral">{liveStatus}</Tag>
              <Text color="secondary" type="b100">
                {t('asset_schedule_bulk_member_count', {
                  count: group.assetKeys.length,
                })}
              </Text>
              {isDirty && (
                <Tag
                  data-testid={`bulk-schedule-dirty-${group.groupId}`}
                  type="warning"
                >
                  {t('asset_schedule_bulk_edited')}
                </Tag>
              )}
            </Row>
            <Text
              color="secondary"
              data-testid={isMixed
                ? `bulk-schedule-mixed-${group.groupId}`
                : `bulk-schedule-summary-${group.groupId}`}
              type="b200"
            >
              {scheduleSummary}
            </Text>
          </Column>
          <Button
            aria-controls={contentId}
            aria-expanded={isExpanded}
            aria-label={t(toggleLabelKey, {
              status: liveStatus,
              title: group.titleName,
            })}
            data-testid={`bulk-schedule-toggle-${group.groupId}`}
            id={toggleId}
            size="small"
            type="icon"
            onClick={onToggle}
          >
            <Icon
              tokens={isExpanded
                ? chevronDownSmallTokens
                : chevronRightSmallTokens}
            />
          </Button>
        </Row>
        {isExpanded && (
          <Box id={contentId} width="100%">
            <Column spacing="200">
              <ScheduleInputGroup
                endTime={schedule.endTime}
                startTime={schedule.startTime}
                testIdPrefix={`bulk-schedule-${group.groupId}`}
                onEndTimeChange={(value) => { onChange('endTime', value); }}
                onStartTimeChange={(value) => { onChange('startTime', value); }}
              />
              <Row alignmentHorizontal="end">
                <Button
                  aria-label={t('asset_schedule_bulk_reset_group', {
                    status: liveStatus,
                    title: group.titleName,
                  })}
                  data-testid={`bulk-schedule-reset-${group.groupId}`}
                  disabled={!isDirty}
                  size="small"
                  type="link"
                  onClick={onReset}
                >
                  {t('asset_schedule_bulk_reset')}
                </Button>
              </Row>
            </Column>
          </Box>
        )}
        {errorKey && (
          <Text
            color="error"
            data-testid={`bulk-schedule-error-${group.groupId}`}
            id={errorId}
            role="alert"
            type="b100"
          >
            {t(errorKey)}
          </Text>
        )}
      </Column>
    </Box>
  );
};
